import React, { useState, useRef, useEffect, useCallback } from 'react';
import { SortDropdownProps, SortOption } from './types';
import styles from '@/styles/components/models-gallery/SortDropdown.module.css';

const SORT_OPTIONS: { value: SortOption; label: string; description: string }[] = [
  { 
    value: 'most-relevant', 
    label: 'Most Relevant', 
    description: 'Based on search query and preferences' 
  },
  { 
    value: 'popularity', 
    label: 'Popularity', 
    description: 'Most used models first' 
  },
  { 
    value: 'price-low-high', 
    label: 'Price: Low to High', 
    description: 'Cheapest models first' 
  },
  { 
    value: 'price-high-low', 
    label: 'Price: High to Low', 
    description: 'Most expensive models first' 
  }
];

const SortDropdown: React.FC<SortDropdownProps> = ({ value, onChange, className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const selectedOption = SORT_OPTIONS.find(option => option.value === value) || SORT_OPTIONS[0];

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  const handleOptionSelect = useCallback((option: SortOption) => {
    onChange(option);
    setIsOpen(false);
    triggerRef.current?.focus();
  }, [onChange]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleToggle();
    } else if (event.key === 'Escape') {
      setIsOpen(false);
      triggerRef.current?.focus();
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
      } else {
        // Navigate through options
        const currentIndex = SORT_OPTIONS.findIndex(opt => opt.value === value);
        let nextIndex;
        
        if (event.key === 'ArrowDown') {
          nextIndex = currentIndex < SORT_OPTIONS.length - 1 ? currentIndex + 1 : 0;
        } else {
          nextIndex = currentIndex > 0 ? currentIndex - 1 : SORT_OPTIONS.length - 1;
        }
        
        handleOptionSelect(SORT_OPTIONS[nextIndex].value);
      }
    }
  }, [handleToggle, isOpen, value, handleOptionSelect]);

  const handleOptionKeyDown = useCallback((event: React.KeyboardEvent, option: SortOption) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleOptionSelect(option);
    }
  }, [handleOptionSelect]);

  return (
    <div className={`${styles.sortDropdown} ${className || ''}`} ref={dropdownRef}>
      <label className={styles.sortLabel} id="sort-label">
        Sort by:
      </label>
      
      <button
        ref={triggerRef}
        type="button"
        className={`${styles.sortTrigger} ${isOpen ? styles.sortTriggerOpen : ''}`}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-labelledby="sort-label sort-selected"
        aria-describedby="sort-help"
      >
        <span id="sort-selected" className={styles.selectedOption}>
          {selectedOption.label}
        </span>
        
        <svg
          className={`${styles.dropdownIcon} ${isOpen ? styles.dropdownIconOpen : ''}`}
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M3 4.5L6 7.5L9 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <div 
          className={styles.sortOptions}
          role="listbox"
          aria-labelledby="sort-label"
        >
          {SORT_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`${styles.sortOption} ${
                option.value === value ? styles.sortOptionSelected : ''
              }`}
              onClick={() => handleOptionSelect(option.value)}
              onKeyDown={(e) => handleOptionKeyDown(e, option.value)}
              role="option"
              aria-selected={option.value === value}
              tabIndex={0}
            >
              <div className={styles.optionContent}>
                <span className={styles.optionLabel}>{option.label}</span>
                <span className={styles.optionDescription}>{option.description}</span>
              </div>
              
              {option.value === value && (
                <svg
                  className={styles.checkIcon}
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M13.5 4.5L6 12L2.5 8.5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Screen reader help text */}
      <div id="sort-help" className="sr-only">
        Use arrow keys to navigate options when dropdown is open. Press Escape to close.
      </div>
    </div>
  );
};

export default SortDropdown;